import type {
  KaiTablePrefsPatch,
  TableColumnDisplay,
  TableColumnMeta,
  TablePreferences,
  TablePreferencesDefaults,
  TableStickyColumns,
} from "./types";

export const DEFAULT_STICKY_COLUMNS: TableStickyColumns = { first: 0, last: 0 };
export const STICKY_FIRST_MAX = 3;
export const STICKY_LAST_MAX = 2;
export const MIN_COLUMN_WIDTH_PX = 56;
export const MAX_COLUMN_WIDTH_PX = 640;

export function parseColumnWidthPx(width: string): number {
  const parsed = Number.parseInt(width, 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function clampColumnWidthPx(widthPx: number): number {
  if (!Number.isFinite(widthPx)) return MIN_COLUMN_WIDTH_PX;
  return Math.min(MAX_COLUMN_WIDTH_PX, Math.max(MIN_COLUMN_WIDTH_PX, Math.round(widthPx)));
}

export function columnWidthPx(
  column: TableColumnMeta,
  columnWidths?: Record<string, number>,
): number {
  const override = columnWidths?.[column.id];
  if (typeof override === "number" && Number.isFinite(override)) {
    return clampColumnWidthPx(override);
  }
  return clampColumnWidthPx(parseColumnWidthPx(column.width));
}

export function applyColumnWidths<T extends TableColumnMeta>(
  columns: readonly T[],
  columnWidths?: Record<string, number>,
): T[] {
  return columns.map((column) => ({
    ...column,
    width: `${columnWidthPx(column, columnWidths)}px`,
  }));
}

export function normalizeColumnWidths(
  stored: Record<string, number> | undefined,
  columns: readonly TableColumnMeta[],
): Record<string, number> | undefined {
  if (!stored || typeof stored !== "object") return undefined;
  const knownIds = new Set(columns.map((column) => column.id));
  const next: Record<string, number> = {};
  for (const [id, width] of Object.entries(stored)) {
    if (!knownIds.has(id)) continue;
    if (typeof width !== "number" || !Number.isFinite(width)) continue;
    next[id] = clampColumnWidthPx(width);
  }
  return Object.keys(next).length > 0 ? next : undefined;
}

/**
 * Window event dispatched after Kai-applied table prefs are written to
 * localStorage, so a mounted table can refresh in-memory prefs immediately.
 */
export const KAI_TABLE_PREFS_EVENT = "kai-table-prefs";

export function loadStoredPreferences(
  storageKey: string,
): Partial<TablePreferences> | undefined {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return undefined;
    return JSON.parse(raw) as Partial<TablePreferences>;
  } catch {
    return undefined;
  }
}

export function persistPreferences(storageKey: string, prefs: TablePreferences) {
  localStorage.setItem(storageKey, JSON.stringify(prefs));
}

export function defaultVisible(column: TableColumnMeta): boolean {
  if (column.alwaysVisible) return true;
  return column.defaultVisible !== false;
}

export function buildDefaultContentDisplay(
  columns: readonly TableColumnMeta[],
): TableColumnDisplay[] {
  return pinContentDisplay(
    columns.map((column) => ({
      id: column.id,
      visible: defaultVisible(column),
    })),
    columns,
  );
}

export function buildDefaultPreferences(
  columns: readonly TableColumnMeta[],
  defaults: TablePreferencesDefaults = {},
): TablePreferences {
  return {
    pageSize: defaults.pageSize ?? 12,
    wrapLines: defaults.wrapLines ?? false,
    stripedRows: defaults.stripedRows ?? false,
    contentDisplay: buildDefaultContentDisplay(columns),
    groupBy: defaults.groupBy ?? "",
    stickyGroupHeaders: defaults.stickyGroupHeaders ?? false,
    stickyColumns: normalizeStickyColumns(
      defaults.stickyColumns,
      DEFAULT_STICKY_COLUMNS,
    ),
    showSummaryRow: defaults.showSummaryRow ?? false,
    resizableColumns: defaults.resizableColumns ?? false,
  };
}

export function clampSticky(
  value: number | undefined,
  fallback: number,
  max: number,
): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(0, Math.round(value)));
}

export function normalizeStickyColumns(
  stored: Partial<TableStickyColumns> | undefined,
  fallback: TableStickyColumns = DEFAULT_STICKY_COLUMNS,
): TableStickyColumns {
  return {
    first: clampSticky(stored?.first, fallback.first, STICKY_FIRST_MAX),
    last: clampSticky(stored?.last, fallback.last, STICKY_LAST_MAX),
  };
}

export interface StickyColumnPlacement {
  id: string;
  edge: "first" | "last";
  offset: number;
  /** Innermost sticky column on this edge (draws the freeze shadow). */
  isEdge: boolean;
}

/** Resolve sticky first/last placements for the currently visible columns. */
export function resolveStickyPlacements(
  columns: readonly TableColumnMeta[],
  sticky: TableStickyColumns,
): Map<string, StickyColumnPlacement> {
  const first = Math.min(sticky.first, columns.length);
  const last = Math.min(sticky.last, Math.max(0, columns.length - first));
  const placements = new Map<string, StickyColumnPlacement>();

  let left = 0;
  for (let i = 0; i < first; i++) {
    const column = columns[i];
    if (!column) continue;
    placements.set(column.id, {
      id: column.id,
      edge: "first",
      offset: left,
      isEdge: i === first - 1,
    });
    left += Number.parseInt(column.width, 10) || 0;
  }

  let right = 0;
  for (let i = 0; i < last; i++) {
    const column = columns[columns.length - 1 - i];
    if (!column) continue;
    placements.set(column.id, {
      id: column.id,
      edge: "last",
      offset: right,
      isEdge: i === last - 1,
    });
    right += Number.parseInt(column.width, 10) || 0;
  }

  return placements;
}

export function pinContentDisplay(
  display: readonly TableColumnDisplay[],
  columns: readonly TableColumnMeta[],
): TableColumnDisplay[] {
  const metaById = new Map(columns.map((column) => [column.id, column]));
  const first: TableColumnDisplay[] = [];
  const middle: TableColumnDisplay[] = [];
  const last: TableColumnDisplay[] = [];

  for (const item of display) {
    const pin = metaById.get(item.id)?.pin;
    if (pin === "first") first.push(item);
    else if (pin === "last") last.push(item);
    else middle.push(item);
  }

  return [...first, ...middle, ...last];
}

/**
 * Reconcile stored column prefs with the current column registry:
 * drop unknown ids, append new columns, force always-visible on.
 */
export function normalizeContentDisplay(
  stored: readonly TableColumnDisplay[] | undefined,
  columns: readonly TableColumnMeta[],
): TableColumnDisplay[] {
  const knownIds = new Set(columns.map((column) => column.id));
  const storedVisible = new Map(
    (stored ?? [])
      .filter((item) => knownIds.has(item.id))
      .map((item) => [item.id, item.visible]),
  );

  const ordered: TableColumnDisplay[] = [];
  for (const item of stored ?? []) {
    const column = columns.find((candidate) => candidate.id === item.id);
    if (!column) continue;
    ordered.push({
      id: item.id,
      visible: column.alwaysVisible ? true : item.visible,
    });
  }

  for (const column of columns) {
    if (storedVisible.has(column.id)) continue;
    ordered.push({ id: column.id, visible: defaultVisible(column) });
  }

  return pinContentDisplay(ordered, columns);
}

export function normalizePreferences(
  stored: Partial<TablePreferences> | undefined,
  columns: readonly TableColumnMeta[],
  defaults: TablePreferencesDefaults = {},
): TablePreferences {
  const fallback = buildDefaultPreferences(columns, defaults);
  const pageSize =
    typeof stored?.pageSize === "number" && stored.pageSize > 0
      ? stored.pageSize
      : fallback.pageSize;

  const allowedGroupBy = defaults.allowedGroupBy;
  const storedGroupBy =
    typeof stored?.groupBy === "string" ? stored.groupBy : fallback.groupBy;
  const groupBy =
    !allowedGroupBy || allowedGroupBy.includes(storedGroupBy)
      ? storedGroupBy
      : fallback.groupBy;

  return {
    pageSize,
    wrapLines: typeof stored?.wrapLines === "boolean" ? stored.wrapLines : fallback.wrapLines,
    stripedRows:
      typeof stored?.stripedRows === "boolean" ? stored.stripedRows : fallback.stripedRows,
    contentDisplay: normalizeContentDisplay(stored?.contentDisplay, columns),
    groupBy,
    stickyGroupHeaders:
      typeof stored?.stickyGroupHeaders === "boolean"
        ? stored.stickyGroupHeaders
        : fallback.stickyGroupHeaders,
    stickyColumns: normalizeStickyColumns(
      stored?.stickyColumns,
      fallback.stickyColumns,
    ),
    showSummaryRow:
      typeof stored?.showSummaryRow === "boolean"
        ? stored.showSummaryRow
        : fallback.showSummaryRow,
    resizableColumns:
      typeof stored?.resizableColumns === "boolean"
        ? stored.resizableColumns
        : fallback.resizableColumns,
    columnWidths: normalizeColumnWidths(stored?.columnWidths, columns),
  };
}

export function resolveVisibleColumns<T extends TableColumnMeta>(
  columns: readonly T[],
  contentDisplay: readonly TableColumnDisplay[],
): T[] {
  const byId = new Map(columns.map((column) => [column.id, column]));
  const visible: T[] = [];

  for (const item of contentDisplay) {
    if (!item.visible) continue;
    const column = byId.get(item.id);
    if (column) visible.push(column);
  }

  if (visible.length > 0) return visible;
  return columns.filter((column) => column.alwaysVisible);
}

export function tableMinWidth(columns: readonly TableColumnMeta[]): number {
  return columns.reduce((sum, column) => sum + parseColumnWidthPx(column.width), 0);
}

export function reorderContentDisplay(
  display: readonly TableColumnDisplay[],
  fromId: string,
  toId: string,
  columns: readonly TableColumnMeta[],
): TableColumnDisplay[] {
  if (fromId === toId) return [...display];

  const metaById = new Map(columns.map((column) => [column.id, column]));
  if (metaById.get(fromId)?.pin || metaById.get(toId)?.pin) {
    return [...display];
  }

  const fromIndex = display.findIndex((item) => item.id === fromId);
  const toIndex = display.findIndex((item) => item.id === toId);
  if (fromIndex < 0 || toIndex < 0) return [...display];

  const next = [...display];
  const [moved] = next.splice(fromIndex, 1);
  if (!moved) return [...display];
  next.splice(toIndex, 0, moved);
  return pinContentDisplay(next, columns);
}

export function groupRows<T>(
  rows: readonly T[],
  getGroupKey: (row: T) => string,
): { key: string; rows: T[] }[] {
  const order: string[] = [];
  const buckets = new Map<string, T[]>();

  for (const row of rows) {
    const key = getGroupKey(row) || "—";
    const bucket = buckets.get(key);
    if (bucket) {
      bucket.push(row);
    } else {
      buckets.set(key, [row]);
      order.push(key);
    }
  }

  return order.map((key) => ({ key, rows: buckets.get(key) ?? [] }));
}

export function setColumnVisible(
  display: readonly TableColumnDisplay[],
  id: string,
  visible: boolean,
  columns: readonly TableColumnMeta[],
): TableColumnDisplay[] {
  const column = columns.find((candidate) => candidate.id === id);
  const normalized = normalizeContentDisplay(display, columns);
  if (column?.alwaysVisible) return normalized;

  return normalized.map((item) =>
    item.id === id ? { ...item, visible } : item,
  );
}

/**
 * Apply a Kai show / hide / reorder patch to an existing `contentDisplay`.
 * Unknown ids are ignored. A shown column missing from the current layout is
 * appended as visible. Partial `column_order` lists come first; remaining
 * entries keep their existing order (so pinned/synthetic columns survive).
 */
export function mergeContentDisplay(
  current: readonly TableColumnDisplay[],
  patch: Pick<KaiTablePrefsPatch, "show_columns" | "hide_columns" | "column_order">,
  knownIds: readonly string[],
): TableColumnDisplay[] {
  const known = new Set(knownIds);
  let display: TableColumnDisplay[] = current.map((column) => ({ ...column }));

  const setVisible = (ids: string[] | undefined, visible: boolean) => {
    if (!ids) return;
    for (const id of ids) {
      if (!known.has(id)) continue;
      const entry = display.find((column) => column.id === id);
      if (entry) {
        entry.visible = visible;
      } else if (visible) {
        display.push({ id, visible: true });
      }
    }
  };

  setVisible(patch.show_columns, true);
  setVisible(patch.hide_columns, false);

  if (patch.column_order && patch.column_order.length > 0) {
    const seen = new Set<string>();
    const reordered: TableColumnDisplay[] = [];
    for (const id of patch.column_order) {
      if (seen.has(id)) continue;
      const entry = display.find((column) => column.id === id);
      if (entry) {
        reordered.push(entry);
        seen.add(id);
      }
    }
    for (const entry of display) {
      if (!seen.has(entry.id)) {
        reordered.push(entry);
        seen.add(entry.id);
      }
    }
    display = reordered;
  }

  return display;
}
